"use client";

import { useEffect } from "react";

/**
 * Fires a single `POST /products/log-product-click` on mount for product-view
 * analytics. Keyword trends are recorded separately from explicit search
 * interactions; simply opening a product must not count as a keyword search.
 *
 * Why this exists (read this before deleting):
 *
 * The product detail page is server-side cached via Next.js `"use cache"`
 * + `cacheLife("days")`. That cached fetch to /public-search-product-by-uid
 * is ANONYMOUS — no Authorization header is forwarded — so any click-write
 * side effect inside `getProductByIDPublic` cannot be attributed to the
 * signed-in doctor.
 *
 * This tracker runs on the client AFTER hydration, attaches the JWT from
 * the `token` cookie, and POSTs a lightweight click record that the backend
 * can attribute to the real user. SessionStorage de-dupes within a tab so
 * SPA back/forward doesn't double-count.
 */
const ProductViewTracker = ({ productAlias, type }) => {
    useEffect(() => {
        if (!productAlias || !type || typeof window === "undefined") return;

        // De-dupe across rapid back/forward on the same product+type.
        const key = `product_view:${type}:${productAlias}`;
        try {
            if (window.sessionStorage?.getItem(key)) return;
            // Mark before starting the request. In development React can
            // mount effects twice before the first fetch resolves; marking
            // only in `.then()` allowed two identical activity rows.
            window.sessionStorage?.setItem(key, "pending");
        } catch (_) {
            // sessionStorage unavailable (private-mode/Safari) — non-fatal,
            // we just may double-count in that edge case.
        }

        // Read JWT from cookie the same way the rest of the FE does
        // (cookies → `token=...`). When the user is anonymous we still
        // fire the call so raw traffic counts stay accurate — the BE
        // just writes a click without a userId/location in that case.
        let jwtCookie = null;
        try {
            const cookies = document.cookie.split(";");
            cookies.forEach((cookie) => {
                if (cookie.trim().startsWith("token=")) {
                    jwtCookie = "Bearer " + cookie.split("token=")[1];
                }
            });
        } catch (_) {
            /* ignore cookie parse errors */
        }

        const headers = { "Content-Type": "application/json" };
        if (jwtCookie) headers.Authorization = jwtCookie;

        // Pass cached IP if the FE root layout (IpFetcher) populated it.
        let ip = "";
        try {
            ip = window.localStorage?.getItem("user_ip") || "";
        } catch (_) {
            /* ignore */
        }

        fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/products/log-product-click`, {
            method: "POST",
            headers,
            body: JSON.stringify({ productAlias, type, ip }),
            // Keep the activity write alive when the doctor immediately uses
            // the sidebar/back button to return to the dashboard. Without
            // this, a fast navigation can cancel the request before For You
            // has a Zepbound (or other product) view to personalize from.
            keepalive: true,
        })
            .then((response) => {
                if (!response.ok) throw new Error(`View tracking failed (${response.status})`);
                try {
                    window.sessionStorage?.setItem(key, "1");
                } catch (_) {
                    /* sessionStorage unavailable */
                }
            })
            .catch((err) => {
                try {
                    window.sessionStorage?.removeItem(key);
                } catch (_) {
                    /* sessionStorage unavailable */
                }
                // Never throw — analytics is best-effort.
                console.warn("[product view tracker]", err?.message || err);
            });
    }, [productAlias, type]);

    return null;
};

export default ProductViewTracker;
